import { Guid } from "../types/guid";
import { SimpleName } from "./orgadmin/simple-name";

export enum ForumThreadType {
  Discussion = 1,
  Announcement = 2,
}

export interface ForumThread {
  id: Guid;
  title: string;
  content: string;
  attachments?: ForumAttachment[];
  content_preview?: string;
  type: ForumThreadType;
  visibility: number;
  is_locked: boolean;
  is_archived?: boolean;
  is_favorite?: boolean;
  comments_count?: number;
  reads_count?: number;
  read_at?: string | null;
  author?: {
    id: Guid;
    name: string;
  };
  last_activity_at?: string;
  audience_user_ids: string[];
  is_deleted?: boolean;
  created_at?: string;
  edited_at?: string | null;
  updated_at?: string;
  archived_at?: string | null;
  is_pinned?: boolean;
  pinned_at?: string | null;
  can_archive?: boolean;
  archive_available_until?: string | null;
  reactions_summary?: ForumReactionSummary[];
}

export interface ForumUserBasic {
  id: Guid;
  first_name: string;
  last_name: string;
}

export interface ForumUser extends ForumUserBasic {
  email?: string;
}

export interface ForumMember extends ForumUser {
  role: "teacher" | "student";
}

export interface ForumOverviewGroup {
  group: SimpleName;
  course: SimpleName;
  members_count: number;
  unread_threads_count: number;
  last_forum_activity_at: string | null;
}

export interface ForumPost {
  id: Guid;
  thread_id?: string;
  content: string;
  attachments?: ForumAttachment[];
  pending_attachments_count?: number;
  is_deleted?: boolean;
  created_at?: string;
  edited_at?: string | null;
  updated_at?: string;
  author?: ForumUserBasic;
  reactions_summary?: ForumReactionSummary[];
}

export interface ForumAttachment {
  id: Guid;
  post_id?: string;
  thread_id?: string;
  filename: string;
  size: number;
  content_type: string;
  s3_key: string;
  s3_download_url?: string;
  created_at?: string;
  uploader?: ForumUserBasic;
}

export interface ForumThreadRead {
  read_at: string;
  user: ForumUser;
}

export interface ForumReactionOption {
  code: string;
  label: string;
}

export interface ForumReactionSummary {
  emoji: string;
  count: number;
  reacted_by_me?: boolean;
}

export type ForumReactionUser = ForumUser;

export interface ForumThreadReactionDetails {
  emoji: string;
  count: number;
  users: ForumReactionUser[];
}

export interface ForumThreadReaction {
  emoji: string;
  user: ForumUserBasic;
  created_at: string;
}

export interface ForumPostReaction {
  emoji: string;
  user: ForumUserBasic;
  created_at: string;
}

export interface ForumThreadCreatePayload {
  title: string;
  content: string;
  type: ForumThreadType;
  visibility: number;
  is_locked: boolean;
  audience_user_ids: string[];
  mentioned_user_ids?: string[];
  send_announcement_email?: boolean;
}

export interface ForumThreadUpdatePayload {
  title?: string;
  content?: string;
  visibility?: number;
  is_locked?: boolean;
  audience_user_ids?: string[];
  mentioned_user_ids?: string[];
}

export interface ForumPostCreatePayload {
  content: string;
  mentioned_user_ids?: string[];
}

export interface ForumPostUpdatePayload {
  content: string;
  mentioned_user_ids?: string[];
}

export interface ForumAttachmentCreatePayload {
  filename: string;
  size: number;
  content_type: string;
  s3_key: string;
}

export interface ForumAttachmentS3UrlResponse {
  s3_key: string;
  s3_url: {
    url: string;
    fields: Record<string, string>;
  };
  s3_url_expiration_date: string;
}

export interface ForumAttachmentConfig {
  max_size: number;
  allowed_extensions: string[];
}

export interface ForumReactionActionPayload {
  emoji: string;
}
